import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import React, { useEffect } from "react";
import { BoardApi } from "../../../services/board-service-api";
import { KModal } from "../../ui-kit/k-modal/k-modal";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaH5 from "../../ui-kit/typography/H5/H5";
import "./archived-issues-modal.scss";
import { boardKeys } from "./board-query-keys";
import { BoardService } from "./board-service";
import { archivedListChanged } from "./change-event-routing";
import { confirmDialog } from "./confirm-service";
import { Board, BoardChangeEvent, IssueCard } from "./types";

interface ArchivedIssuesModalProps {
  open: boolean;
  board: Board;
  onClose: () => void;
}

const ArchivedIssuesModal: React.FC<ArchivedIssuesModalProps> = ({ open, board, onClose }) => {
  // Archived issues load from the server only while the modal is open (enabled: open), cached
  // by TanStack Query; invalidated on board mutations (archive/restore/delete) so it stays current.
  const queryClient: QueryClient = useQueryClient();
  const archivedQuery = useQuery({
    queryKey: boardKeys.archived(board.id),
    queryFn: (): Promise<IssueCard[]> => BoardApi.getArchivedIssues(board.id),
    enabled: open
  });
  const archived: IssueCard[] = archivedQuery.data ?? [];
  useEffect(() => {
    if (!open) return undefined;
    return BoardService.subscribe((event: BoardChangeEvent) => {
      if (archivedListChanged(event)) {
        void queryClient.invalidateQueries({ queryKey: boardKeys.archived(board.id) });
      }
    });
  }, [queryClient, open, board.id]);

  const handleDelete = (issue: IssueCard): void => {
    void confirmDialog({
      title: "Delete issue",
      message: `Permanently delete ${issue.key}? This cannot be undone.`,
      confirmLabel: "Delete",
      danger: true
    }).then((ok: boolean) => {
      if (ok) BoardService.deleteIssue(issue.id);
    });
  };

  return (
    <KModal showModal={open} closeModal={onClose} width={520} header={<KuikaH5>Archived issues</KuikaH5>}>
      <div className="kuika-board-archived">
        {archived.length === 0 ? (
          <div className="kuika-board-archived__empty">Nothing archived on this board.</div>
        ) : (
          archived.map((issue: IssueCard) => (
            <div key={issue.id} className="kuika-board-archived__row">
              <span className={`kuika-board__type-icon kuika-board__type-icon--${issue.type}`} aria-hidden="true" />
              <span className="kuika-board-archived__key">{issue.key}</span>
              <span className="kuika-board-archived__summary" title={issue.summary}>
                {issue.summary}
              </span>
              <span className="kuika-board-archived__date">
                {issue.archivedAt ? dayjs(issue.archivedAt).format("MMM D, YYYY") : ""}
              </span>
              <div className="kuika-board-archived__actions">
                <KuikaButton type="secondary" size="sm" onClick={() => BoardService.restoreIssue(issue.id)}>
                  Restore
                </KuikaButton>
                <KuikaButton type="tertiary" color="neutral" size="sm" onClick={() => handleDelete(issue)}>
                  Delete
                </KuikaButton>
              </div>
            </div>
          ))
        )}
      </div>
    </KModal>
  );
};

export default ArchivedIssuesModal;
